import { MakeLogicType, actions, kea, listeners, path, reducers, selectors } from 'kea'
import { loaders } from 'kea-loaders'

import api from 'lib/api'

export interface DevUser {
    email: string
    is_staff: boolean
    label: string | null
}

export const DEV_LOGIN_SECONDS_SAVED_PER_CLICK = 5

export const DEV_LOGIN_DEFAULT_EMAIL = 'test@posthog.com'

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface devLoginLogicValues {
    devLoginCount: number
    devLoginTimeSavedLabel: string | null
    devUsers: DevUser[]
    devUsersLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface devLoginLogicActions {
    devLogin: (email: string) => {
        email: string
    }
    loadDevUsers: (_: any) => any
    loadDevUsersFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadDevUsersSuccess: (
        devUsers: DevUser[],
        payload?: any
    ) => {
        devUsers: DevUser[]
        payload?: any
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface devLoginLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        devLoginTimeSavedLabel: (devLoginCount: number) => string | null
    }
}

export type devLoginLogicType = MakeLogicType<
    devLoginLogicValues,
    devLoginLogicActions,
    Record<string, any>,
    devLoginLogicMeta
>

export const devLoginLogic = kea<devLoginLogicType>([
    path(['scenes', 'authentication', 'shared', 'devLoginLogic']),
    actions({
        devLogin: (email: string) => ({ email }),
    }),
    reducers({
        devLoginCount: [
            0,
            { persist: true },
            {
                devLogin: (count) => count + 1,
            },
        ],
    }),
    loaders(() => ({
        devUsers: [
            [] as DevUser[],
            {
                // Not fired on an `onMount` because we don't always need it.
                loadDevUsers: async (_, breakpoint) => {
                    breakpoint()
                    try {
                        const response = await api.get<{ users: DevUser[] }>('api/login/dev')
                        // Pin the default seeded user to the top; the rest keep the backend's alphabetical order.
                        const users = [...response.users]
                        const defaultIndex = users.findIndex((u) => u.email === DEV_LOGIN_DEFAULT_EMAIL)
                        if (defaultIndex > 0) {
                            users.unshift(...users.splice(defaultIndex, 1))
                        }
                        return users
                    } catch {
                        // Endpoint is unavailable unless allow_dev_login is set in preflight.
                        return []
                    }
                },
            },
        ],
    })),
    selectors(() => ({
        devLoginTimeSavedLabel: [
            (s) => [s.devLoginCount],
            (devLoginCount: number): string | null => {
                if (devLoginCount === 0) {
                    return null
                }

                const totalSeconds = devLoginCount * DEV_LOGIN_SECONDS_SAVED_PER_CLICK
                if (totalSeconds < 60) {
                    const unit = totalSeconds === 1 ? 'second' : 'seconds'
                    return `You've saved ${totalSeconds} ${unit} by clicking this button.`
                }

                const minutes = Math.floor(totalSeconds / 60)
                const unit = minutes === 1 ? 'minute' : 'minutes'
                return `You've saved ${minutes} ${unit} by clicking this button.`
            },
        ],
    })),
    listeners(() => ({
        devLogin: async ({ email }) => {
            // Dynamic import to avoid a circular dependency: loginLogic statically imports this logic.
            const { loginLogic, handleLoginRedirect } = await import('scenes/authentication/login/loginLogic')
            loginLogic.actions.clearGeneralError()
            try {
                await api.create<any>('api/login/dev', { email })
            } catch (e) {
                const { code, detail } = e as Record<string, any>
                loginLogic.actions.setGeneralError(code || 'dev_login_failed', detail || 'Dev login failed')
                return
            }
            handleLoginRedirect()
            window.location.reload()
        },
    })),
])
